import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { injectIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import LegalFrameworkLayout from '../LegalFrameworkLayout/LegalFrameworkLayout';

import css from './LegalDocument.module.css';

const SUPPORTED_LANGS = ['es', 'en', 'it', 'pt'];

// Some reformatted documents (terms, service fees, cancellation) ship their CSS
// without the Montserrat @import that the rest of the documents include, so the
// app's single Montserrat 500 weight was the only one available and their bold
// headings (700/800) fell back to a non-bold look. Load the full Montserrat
// weight range for every legal document so titles render bold and consistently.
const fontCss = `@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&display=swap');`;

// Each inline legal/FAQ icon renders as a self-contained green tile with white
// strokes, so it stays visible on any background (the icon containers may sit on
// light-green, white or coloured surfaces). Using currentColor keeps the SVG
// markup free of hardcoded colors.
const legalIconCss = `
  .legalDoc .cat-icon {
    background: transparent;
    width: auto;
    height: auto;
    min-width: 0;
  }

  .legalDoc .legal-icon {
    box-sizing: content-box;
    width: 22px;
    height: 22px;
    padding: 9px;
    border-radius: 12px;
    background: var(--green-dark, #5FBB87);
    color: var(--white, #fff);
    fill: none;
    stroke: currentColor;
    flex: none;
  }

  .legalDoc .legal-icon * {
    stroke: currentColor;
    fill: none;
  }

  .legalDoc .legal-icon *[fill]:not([fill="none"]) {
    fill: currentColor;
  }
`;

// Renders a designed legal document (exported HTML + scoped CSS) inside the
// ÛUPO layout. The document's own styles are scoped to `.legalDoc` at build
// time, so they don't clash with the app's global styles.
const LegalDocumentComponent = props => {
  const { data, language, scrollingDisabled, currentPage, showSidebar } = props;
  const lang = SUPPORTED_LANGS.includes(language) ? language : 'es';

  const title = (data.title && (data.title[lang] || data.title.es)) || 'ÛUPO';
  const html = data.html[lang] || data.html.es;
  const docCss = data.cssShared || (data.css && (data.css[lang] || data.css.es)) || '';

  return (
    <LegalFrameworkLayout
      title={title}
      scrollingDisabled={scrollingDisabled}
      currentPage={currentPage}
      contentClassName={css.documentContent}
      showSidebar={showSidebar}
      showMobileNav={showSidebar}
    >
      <style dangerouslySetInnerHTML={{ __html: fontCss }} />
      <style dangerouslySetInnerHTML={{ __html: docCss }} />
      <style dangerouslySetInnerHTML={{ __html: legalIconCss }} />
      <div className={css.documentFrame}>
        <div className="legalDoc" dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    </LegalFrameworkLayout>
  );
};

LegalDocumentComponent.defaultProps = {
  currentPage: null,
  showSidebar: true,
};

LegalDocumentComponent.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.object,
    html: PropTypes.object.isRequired,
    cssShared: PropTypes.string,
    css: PropTypes.object,
  }).isRequired,
  currentPage: PropTypes.string,
  language: PropTypes.string.isRequired,
  scrollingDisabled: PropTypes.bool.isRequired,
  showSidebar: PropTypes.bool,
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
  language: state.UI.language,
});

export default compose(
  connect(mapStateToProps),
  injectIntl
)(LegalDocumentComponent);
